import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { cmpCertificate, logo, prsCertificate, propertymarkCertificate, qualificationLogo } from "src/assets/assetsExport";
import { companyInfo } from "../../data/general";

interface Footer2Props {}

export default function Footer2(props: Footer2Props): JSX.Element {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    // Check localStorage for saved preference, default to false
    const saved = localStorage.getItem("footerDarkMode");
    return saved ? JSON.parse(saved) : false;
  });

  const services = [
    {
      label: "Rent",
      to: "/services/rent",
    },
    {
      label: "Landlords",
      to: "/services/landlords",
    },
    {
      label: "Stamp Duty Calculator",
      to: "/services/stamp-duty-calculator",
    },
    {
      label: "Rent Estimate",
      to: "/services/rent-estimate",
    },
    {
      label: "Mortgage Calculator",
      to: "/services/mortgage-calculator",
    },
    {
      label: "Rental Yield",
      to: "/services/rental-yield",
    },
  ];

  const agents = [
    {
      label: "About Us",
      to: "/about-us",
    },
    {
      label: "Our Team",
      to: "/about-us/team",
    },
    {
      label: "Compliance",
      to: "/about-us#regulatory-stack",
    },
    {
      label: "Tenant Guides",
      to: "/guides/tenants",
    },
    {
      label: "Contact Us",
      to: "/contact-us",
    },
    {
      label: "Careers",
      to: "/careers",
    },
    {
      label: "Insights",
      to: "/insights",
    },
  ];

  const terms = [
    {
      label: "Terms and Conditions",
      to: "/policies/terms-and-conditions",
    },
    {
      label: "Privacy Policy",
      to: "/policies/privacy-policy",
    },
    {
      label: "Cookies Policy",
      to: "/policies/cookies-policy",
    },
  ];

  useEffect(() => {
    // Save preference to localStorage whenever it changes
    localStorage.setItem("footerDarkMode", JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  function FooterItem({ service }: { service: any }) {
    return (
      <li className="mb-3" key={service.to}>
        <NavLink
          to={service.to}
          className={`font-manrope text-base relative inline-block transition-all duration-300 hover:translate-x-1 group ${
            isDarkMode
              ? "text-[#888] hover:text-brand-yellow"
              : "text-text-slate hover:text-text-obsidian"
          }`}
        >
          {service.label}
        </NavLink>
      </li>
    );
  }

  function CertItem({ item }: { item: { title: string; logo: string; url: string } }) {
    const className = `px-4 py-2.5 cursor-pointer transform skew-x-[var(--skew)] transition-all duration-300 flex items-center justify-center min-w-[60px] group ${
      isDarkMode
        ? "bg-[rgba(255,255,255,0.05)] border border-solid border-[rgba(255,255,255,0.1)] hover:border-brand-yellow hover:bg-[rgba(255,196,0,0.1)] hover:shadow-[0_0_15px_rgba(255,196,0,0.2)]"
        : "bg-bg-white border border-solid border-[rgba(0,0,0,0.1)] hover:border-brand-yellow hover:shadow-[0_5px_15px_rgba(0,0,0,0.05)] hover:bg-text-obsidian"
    }`;

    const content = (
      <div
        className={`block transform skew-x-[10deg] font-teko text-xl group-hover:text-brand-yellow ${
          isDarkMode ? "text-[#888]" : "text-text-slate"
        }`}
      >
        <img
          src={item.logo}
          alt={`${item.title} logo`}
          className={`h-10 p-1 ${
            isDarkMode
              ? "filter invert brightness-0 saturate-0 opacity-60"
              : ""
          }`}
        />
      </div>
    );

    return (
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        title={item.title}
      >
        {content}
      </a>
    );
  }

  function TermItem({ item }: { item: any }) {
    return (
      <Link
        to={item.to}
        className={`hover:underline transition-all duration-300 ${
          isDarkMode ? "hover:text-white" : "hover:text-text-obsidian"
        }`}
      >
        {item.label}
      </Link>
    );
  }

  const labelCss = `font-teko uppercase tracking-wide mb-6 flex items-center gap-2.5 text-xl ${
    isDarkMode ? "text-[#888] tracking-widest" : "tracking-wide"
  }`;

  const labelDecoCss = "bg-brand-yellow transform skew-x-[var(--skew)] w-1 h-4";

  return (
    <footer
      className={`${
        isDarkMode
          ? "bg-[#0A0A0A] text-white border-t-4 border-t-brand-yellow"
          : "bg-[#E5E5E5] text-text-obsidian border-t border-t-solid border-t-[rgba(0,0,0,0.1)]"
      } pt-24 pb-8 px-[5%] relative overflow-hidden`}
    >
      {/* Background Grid Overlay */}
      <div
        className={`absolute top-0 left-0 w-full h-full pointer-events-none z-0 ${
          isDarkMode
            ? "bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:60px_60px]"
            : "bg-[linear-gradient(rgba(0,0,0,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.05)_1px,transparent_1px)] bg-[length:40px_40px]"
        }`}
      ></div>

      {/* Dark Mode Toggle Button */}
      <div className="max-w-[1600px] mx-auto relative z-20 mb-4 flex justify-end">
        <button
          type="button"
          onClick={toggleDarkMode}
          className={`font-manrope text-xs uppercase tracking-wider px-4 py-2 border border-solid transition-all duration-300 transform skew-x-[var(--skew)] ${
            isDarkMode
              ? "border-[rgba(255,255,255,0.2)] text-white hover:border-brand-yellow hover:bg-[rgba(255,255,255,0.05)]"
              : "border-[rgba(0,0,0,0.1)] text-text-obsidian hover:border-brand-yellow hover:bg-white"
          }`}
          aria-label="Toggle dark mode"
        >
          <span className="block transform skew-x-[10deg]">
            {isDarkMode ? "Light" : "Dark"}
          </span>
        </button>
      </div>

      {/* Main Grid */}
      <div className="max-w-[1600px] mx-auto relative z-10 grid grid-cols-1 gap-12 lg:grid-cols-[1.5fr_1fr_1fr_1.5fr] lg:gap-16 mb-20 ">
        {/* Col 1: Brand & Mission */}
        <div className="f-col">
          <img
            src={isDarkMode ? logo.light : logo.dark}
            alt="Letting Engine logo"
            className="h-14"
          />
          <p
            className={`font-manrope text-sm leading-relaxed max-w-[300px] mt-8 ${
              isDarkMode
                ? "text-[#888] border-l border-l-solid border-l-[rgba(255,255,255,0.1)] pl-4"
                : "text-text-slate"
            }`}
          >
            London-based residential letting agency specialising in luxury
            rentals, matching discerning tenants with exceptional homes in the
            city's most sought-after locations.
          </p>
          <div className="flex gap-4 mt-6">
            <a
              href="https://www.linkedin.com/company/letting-engine"
              target="_blank"
              rel="noopener noreferrer"
              className={`font-manrope text-xs uppercase tracking-wider transition-colors ${
                isDarkMode
                  ? "text-[#888] hover:text-brand-yellow"
                  : "text-text-slate hover:text-text-obsidian"
              }`}
              aria-label="Letting Engine on LinkedIn"
            >
              LinkedIn
            </a>
            <a
              href="https://www.instagram.com/lettingengine"
              target="_blank"
              rel="noopener noreferrer"
              className={`font-manrope text-xs uppercase tracking-wider transition-colors ${
                isDarkMode
                  ? "text-[#888] hover:text-brand-yellow"
                  : "text-text-slate hover:text-text-obsidian"
              }`}
              aria-label="Letting Engine on Instagram"
            >
              Instagram
            </a>
          </div>
        </div>

        {/* Col 2: Navigation */}
        <div className="f-col">
          <div className={labelCss}>
            <div className={labelDecoCss}></div>
            Services
          </div>
          <ul className="list-none p-0">
            {services.map((service) => (
              <FooterItem key={service.to} service={service} />
            ))}
          </ul>
        </div>

        {/* Col 3: Company */}
        <div className="f-col">
          <div className={labelCss}>
            <div className={labelDecoCss}></div>
            About Us
          </div>
          <ul className="list-none p-0">
            {agents.map((service) => (
              <FooterItem key={service.to} service={service} />
            ))}
          </ul>
        </div>

        {/* Col 4: Contact & Quals */}
        <div className="f-col">
          <div className={labelCss}>
            <div className={labelDecoCss}></div>
            Contact Us
          </div>

          <div className="mb-8">
            <span
              className={`font-teko uppercase block mb-1 tracking-wide text-lg ${
                isDarkMode ? "text-white" : "text-text-obsidian"
              }`}
            >
              HQ Location
            </span>
            <a
              href="https://www.google.com/maps/search/?api=1&query=16+Berkeley+Street,+Mayfair,+London+W1J+8DZ"
              target="_blank"
              rel="noopener noreferrer"
              className={`font-manrope text-base hover:text-brand-yellow transition-colors ${
                isDarkMode ? "text-[#888] font-light" : "text-text-slate"
              }`}
            >
              16 Berkeley Street
              <br />
              Mayfair, London
              <br />
              W1J 8DZ, UK
            </a>
          </div>

          <div className="mb-6">
            <span
              className={`font-teko uppercase block mb-1 tracking-wide text-lg ${
                isDarkMode ? "text-white" : "text-text-obsidian"
              }`}
            >
              Comms
            </span>
            <span className={`font-manrope text-base`}>
              <a
                href={`tel:${companyInfo.phone.replace(/[^\d+]/g, "")}`}
                className="text-[#888] hover:text-brand-yellow transition-colors"
              >
                {companyInfo.phone}
              </a>
              <br />
              <a
                href={`mailto:${companyInfo.email}`}
                className="text-[#888] hover:text-brand-yellow transition-colors"
              >
                {companyInfo.email}
              </a>
            </span>
          </div>

          <div className={`${labelCss} mt-20`}>
            <div className={labelDecoCss}></div>A Member of
          </div>
          <div className="flex gap-2.5 flex-wrap">
            <CertItem
              item={{
                title: "Property Mark",
                logo: qualificationLogo.propertymark,
                url: propertymarkCertificate,
              }}
            />
            <CertItem
              item={{
                title: "CMP",
                logo: qualificationLogo.cmp,
                url: cmpCertificate,
              }}
            />
            <CertItem
              item={{
                title: "PRS",
                logo: qualificationLogo.prs,
                url: prsCertificate,
              }}
            />
            <CertItem
              item={{
                title: "DPS",
                logo: qualificationLogo.dps,
                url: "https://www.depositprotection.com/",
              }}
            />
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div
        className={`max-w-[1600px] mx-auto relative z-10 border-t border-t-solid pt-8 flex justify-between items-center lg:flex-col lg:items-start lg:gap-8 ${
          isDarkMode
            ? "border-t-[rgba(255,255,255,0.1)]"
            : "border-t-[rgba(0,0,0,0.1)]"
        }`}
      >
        <div
          className={`font-manrope flex gap-8 flex-wrap ${
            isDarkMode
              ? "text-xs text-[#555] uppercase tracking-widest"
              : "text-xs text-[#888]"
          }`}
        >
          {terms.map((item) => (
            <TermItem item={item} key={item.to} />
          ))}
        </div>
        <div
          className={`font-manrope text-xs uppercase tracking-wide text-right lg:text-left ${
            isDarkMode ? "text-[#444] leading-relaxed" : "text-[#888]"
          }`}
        >
          <div className="normal-case">
            Letting Engine is a trading name of New Vision Real Estate Ltd,
            which is registered in England &amp; Wales under registered company
            number: 12987204, registered office address: 16 Berkeley Street,
            Mayfair, London, W1J 8DZ
          </div>
          <div>© Copyright {new Date().getFullYear()} Letting Engine</div>
        </div>
      </div>
    </footer>
  );
}
